import {
	PredefinedConfiguration,
	RangeSettings,
	MissingProductItem,
	SofaConfigurationItem,
	Settings,
} from 'sofa-reborn-react';

import { ProductUtils } from '../../../util/ProductUtils';
import ThunksUtil from '../utils/ThunksUtil';

import { IProduct } from '@inter-ikea-kompis/types';
import { ProductService, KompisDefaultFields } from '@inter-ikea-kompis/services';
import { ProductData } from '@ikea-aoa/component-view-3d';

export default class ProductVariantParser {
	public async parse(
		products: ProductData,
		missingProducts: MissingProductItem[],
		rangeSettings: RangeSettings,
		predefinedConfigurations: PredefinedConfiguration[],
		allPredefinedConfigurations: PredefinedConfiguration[],
		fetchedProducts: IProduct[],
		settings: Settings
	): Promise<string[]> {
		const allIds: string[] = [];

		const allModelIdsForConfiguration = allPredefinedConfigurations.map((configuration) => ({
			configurationId: configuration.id,
			modelIds: ProductUtils.getModelIdsForConfiguration(
				products,
				missingProducts,
				configuration.configuration.children as SofaConfigurationItem[],
				rangeSettings.shouldColorAllModules as boolean,
				true
			),
		}));

		await Promise.all(
			predefinedConfigurations.map((predefinedConfiguration, index) =>
				this.assignProductVariants(
					products,
					missingProducts,
					rangeSettings,
					allModelIdsForConfiguration,
					fetchedProducts,
					predefinedConfiguration,
					predefinedConfigurations,
					allIds,
					index,
					settings
				)
			)
		);
		return allIds;
	}

	private async assignProductVariants(
		products: ProductData,
		missingProducts: MissingProductItem[],
		rangeSettings: RangeSettings,
		allModelIdsForConfiguration: { configurationId: string | undefined; modelIds: string[] }[],
		fetchedProducts: IProduct[],
		predefinedConfiguration: PredefinedConfiguration,
		predefinedConfigurations: PredefinedConfiguration[],
		allIds: string[],
		index: number,
		settings: Settings
	): Promise<void> {
		let fetchedIkeaProducts: IProduct[] = [];
		const ids: string[] = [];
		const configurationModelIds = ProductUtils.getModelIdsForConfiguration(
			products,
			missingProducts,
			predefinedConfiguration.configuration.children as SofaConfigurationItem[],
			rangeSettings.shouldColorAllModules as boolean,
			true
		);
		allModelIdsForConfiguration.forEach(({ configurationId, modelIds }) => {
			if (configurationId && this.arraysEqual(configurationModelIds.sort(), modelIds.sort())) {
				const addVariant = predefinedConfiguration.id
					? predefinedConfiguration.id !== configurationId
					: predefinedConfiguration.localId
					? predefinedConfiguration.localId !== configurationId
					: false;
				if (addVariant) {
					ids.push(configurationId);
					allIds.push(configurationId);
				}
			}
		});
		if (ids.length > 0) {
			fetchedIkeaProducts = this.fetchProductVariants(
				ids,
				fetchedProducts,
				rangeSettings,
				settings
			);
		}
		predefinedConfigurations[index]['productVariants'] = fetchedIkeaProducts || [];
	}

	private arraysEqual(configurationModelIds: string[], modelIds: string[]): boolean {
		if (configurationModelIds === modelIds) {
			return true;
		}
		if (configurationModelIds == null || modelIds == null) {
			return false;
		}
		if (configurationModelIds.length !== modelIds.length) {
			return false;
		}

		for (let i = 0; i < configurationModelIds.length; ++i) {
			if (configurationModelIds[i] !== modelIds[i]) {
				return false;
			}
		}
		return true;
	}

	private fetchProductVariants(
		ids: string[],
		fetchedProducts: IProduct[],
		rangeSettings: RangeSettings,
		settings: Settings
	): IProduct[] {
		const products: IProduct[] = [];
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const productService = new ProductService({
			locale: settings.locale,
			localizationPrefixApplication: settings.localizationPrefixApplication,
			localizationEndpoints: settings.localizationEndpoints,
		});
		ids.forEach((id) => {
			const prod = fetchedProducts.find((prod) => id === ThunksUtil.getProductItemId(prod, false));
			if (
				(prod &&
					prod.content &&
					prod.content.child?.every((child) =>
						fetchedProducts.some(async (product) => {
							if (
								(rangeSettings.localSofabedMattressId &&
									Object.values(rangeSettings.localSofabedMattressId as object).includes(
										child.itemId.slice(4)
									)) ||
								(rangeSettings.localSofabedMattressId &&
									Object.values(rangeSettings.newLocalSofabedMattressId as object).includes(
										child.itemId.slice(4)
									))
							) {
								const mattresses = await productService.getProducts(
									[{ type: 'ART', id: child.itemId.slice(4) }],
									KompisDefaultFields
								);

								if (mattresses.length > 0) {
									return mattresses[0];
								}
							}
							return (
								child.itemId.slice(4) === product.content?.itemNoGlobal ||
								child.itemId.slice(4) === product.content?.itemNoLocal
							);
						})
					)) ||
				(prod && prod.content?.itemType === 'ART' && prod.content.child === null)
			) {
				products.push(prod);
			}
		});
		return products;
	}
}
